(() => {
  const MAIN_PRODUCT_SELECTOR = '[id^="MainProduct-"]';
  const PICKER_SELECTOR = 'variant-selects, variant-radios';
  const MOBILE_MEDIA = window.matchMedia('(max-width: 749px)');

  /*
   * After a real swipe is detected, allow slideChanged a short
   * period to arrive. This accommodates momentum scrolling.
   */
  const SWIPE_VALID_MS = 1800;

  /*
   * After a variant change, Dawn may move the gallery itself.
   * During this period, slideChanged must NOT select a variant.
   */
  const PROGRAMMATIC_GALLERY_SUPPRESS_MS = 1200;

  function getProductRoots(root = document) {
    const roots = [];

    if (root.matches?.(MAIN_PRODUCT_SELECTOR)) {
      roots.push(root);
    }

    root.querySelectorAll?.(MAIN_PRODUCT_SELECTOR).forEach((element) => {
      if (!roots.includes(element)) {
        roots.push(element);
      }
    });

    return roots;
  }

  function initProduct(sectionRoot) {
    if (
      !sectionRoot ||
      sectionRoot.dataset.variantImageSyncInitialized === 'true'
    ) {
      return;
    }

    const mapScript = sectionRoot.querySelector(
      'script[data-media-variant-map]'
    );

    const galleries = Array.from(
      sectionRoot.querySelectorAll('media-gallery')
    );

    if (!mapScript || galleries.length === 0) {
      return;
    }

    let entries;

    try {
      entries = JSON.parse(mapScript.textContent);
    } catch (error) {
      console.error(
        'Variant image selector: media map could not be parsed.',
        error
      );
      return;
    }

    /*
     * Build:
     *
     * media target -> variant(s)
     */
    const mediaMap = new Map();

    entries.forEach((entry) => {
      if (
        !entry?.mediaTarget ||
        !entry?.variantId ||
        !Array.isArray(entry.options)
      ) {
        return;
      }

      const key = String(entry.mediaTarget);
      const variants = mediaMap.get(key) || [];

      variants.push(entry);
      mediaMap.set(key, variants);
    });

    /*
     * Each gallery keeps its own gesture state.
     */
    const galleryStates = new WeakMap();

    /*
     * Global to this product section:
     *
     * while this timestamp is in the future,
     * gallery movements are considered Dawn-driven,
     * not user-driven.
     */
    let suppressGalleryUntil = 0;

    function clearSwipeStates() {
      galleries.forEach((gallery) => {
        const state = galleryStates.get(gallery);

        if (!state) {
          return;
        }

        state.swipeUntil = 0;
      });
    }

    function suppressGalleryFeedback() {
      suppressGalleryUntil =
        Date.now() + PROGRAMMATIC_GALLERY_SUPPRESS_MS;

      clearSwipeStates();
    }

    function getCurrentVariantId() {
      const input = sectionRoot.querySelector(
        'product-form input[name="id"], ' +
        'form[id^="product-form-"] input[name="id"]'
      );

      if (input?.value) {
        return String(input.value);
      }

      return (
        new URLSearchParams(window.location.search)
          .get('variant') || ''
      );
    }

    /*
     * Set one option group without triggering Dawn yet.
     *
     * Supports:
     * - radio/pill options
     * - swatches
     * - dropdowns
     */
    function setOptionGroup(group, wantedValue) {
      const select = group.querySelector('select');

      if (select) {
        const wantedOption = Array.from(
          select.options
        ).find(
          (option) => option.value === wantedValue
        );

        if (!wantedOption) {
          return null;
        }

        /*
         * Keep both selected property and selected
         * attribute synchronized for older Dawn versions.
         */
        Array.from(select.options).forEach((option) => {
          option.selected = option === wantedOption;

          if (option === wantedOption) {
            option.setAttribute(
              'selected',
              'selected'
            );
          } else {
            option.removeAttribute('selected');
          }
        });

        select.value = wantedValue;

        return select;
      }

      const radios = Array.from(
        group.querySelectorAll(
          'input[type="radio"]'
        )
      );

      const wantedRadio = radios.find(
        (radio) => radio.value === wantedValue
      );

      if (!wantedRadio) {
        return null;
      }

      wantedRadio.checked = true;

      return wantedRadio;
    }

    /*
     * Select the Shopify variant represented by a gallery image.
     *
     * We never manipulate the main image directly here.
     * Dawn remains responsible for:
     *
     * - variant ID
     * - URL
     * - price
     * - SKU
     * - Add to Cart
     * - selected media
     * - active thumbnail
     */
    function selectVariant(entry) {
      if (
        String(entry.variantId) ===
        getCurrentVariantId()
      ) {
        return;
      }

      /*
       * Dawn can replace its variant picker during updates,
       * so always retrieve the current element.
       */
      const picker = sectionRoot.querySelector(
        PICKER_SELECTOR
      );

      if (!picker) {
        return;
      }

      const groups = Array.from(
        picker.querySelectorAll(
          'fieldset.product-form__input, ' +
          '.product-form__input--dropdown'
        )
      );

      if (groups.length < entry.options.length) {
        console.warn(
          'Variant image selector: option group count mismatch.',
          entry
        );

        return;
      }

      let triggerControl = null;

      /*
       * Set ALL option values first.
       */
      for (
        let index = 0;
        index < entry.options.length;
        index += 1
      ) {
        const control = setOptionGroup(
          groups[index],
          String(entry.options[index])
        );

        if (!control) {
          console.warn(
            'Variant image selector: option value not found:',
            entry.options[index]
          );

          return;
        }

        triggerControl = control;
      }

      if (!triggerControl) {
        return;
      }

      /*
       * Dawn is about to move the gallery because
       * the variant is changing.
       *
       * Make sure that movement cannot feed back into
       * swipe -> variant selection.
       */
      suppressGalleryFeedback();

      /*
       * Fire exactly ONE normal Dawn change event
       * after all option controls have been set.
       */
      triggerControl.dispatchEvent(
        new Event('change', {
          bubbles: true
        })
      );
    }

    /*
     * =====================================================
     * VARIANT PICKER -> GALLERY GUARD
     * =====================================================
     *
     * When the customer changes a normal variant radio,
     * Dawn will subsequently move the gallery.
     *
     * Mark that gallery movement as programmatic BEFORE
     * Dawn handles the change.
     */
    sectionRoot.addEventListener(
      'change',
      (event) => {
        const picker =
          event.target.closest?.(
            PICKER_SELECTOR
          );

        if (
          picker &&
          sectionRoot.contains(picker) &&
          event.target.matches(
            'input[type="radio"], select'
          )
        ) {
          suppressGalleryFeedback();
        }
      },
      true
    );

    galleries.forEach((gallery) => {
      const state = {
        swipeUntil: 0
      };

      galleryStates.set(gallery, state);

      /*
       * =====================================================
       * THUMBNAIL -> VARIANT
       * =====================================================
       *
       * For an image uniquely assigned to one variant,
       * intercept the thumbnail tap BEFORE Dawn's native
       * thumbnail handler.
       *
       * We select the variant instead.
       * Dawn then selects the corresponding main image.
       */
      gallery.addEventListener(
        'click',
        (event) => {
          /*
           * Ignore JavaScript-generated clicks.
           */
          if (!event.isTrusted) {
            return;
          }

          const thumbnailButton =
            event.target.closest(
              'button.thumbnail'
            );

          if (
            !thumbnailButton ||
            !gallery.contains(thumbnailButton)
          ) {
            return;
          }

          const thumbnailItem =
            thumbnailButton.closest(
              '[data-target]'
            );

          if (!thumbnailItem) {
            return;
          }

          const mediaTarget =
            String(
              thumbnailItem.dataset.target || ''
            );

          if (!mediaTarget) {
            return;
          }

          const matches =
            mediaMap.get(mediaTarget);

          /*
           * If there is no variant mapping,
           * let Dawn handle it as ordinary media.
           *
           * If several variants share the image,
           * the image cannot uniquely determine a variant.
           */
          if (
            !matches ||
            matches.length !== 1
          ) {
            return;
          }

          const entry = matches[0];

          /*
           * Already the current variant:
           * let Dawn perform the normal thumbnail action.
           */
          if (
            String(entry.variantId) ===
            getCurrentVariantId()
          ) {
            return;
          }

          /*
           * Stop Dawn from changing the image independently.
           *
           * We change the variant instead; Dawn then
           * changes the image as part of that operation.
           */
          event.preventDefault();
          event.stopPropagation();

          selectVariant(entry);
        },
        true
      );

      /*
      * =====================================================
      * MOBILE SWIPE -> VARIANT
      * =====================================================
      *
      * A real touch/pen interaction with the gallery
      * authorizes the next slideChanged event.
      *
      * Dawn's own programmatic gallery changes are
      * separately suppressed by suppressGalleryFeedback().
      */
      const viewer = gallery.querySelector(
        '[id^="GalleryViewer-"]'
      );

      if (!viewer) {
        return;
      }

      const swipeSurface =
        viewer.slider || viewer;

      /*
      * A real mobile interaction with the gallery means
      * the next slideChanged may select a variant.
      */
      const markUserGalleryInteraction = (event) => {
        if (!MOBILE_MEDIA.matches) {
          return;
        }

        /*
        * Ignore ordinary desktop mouse interaction.
        */
        if (
          event.pointerType === 'mouse' ||
          (
            event.button !== undefined &&
            event.button !== 0
          )
        ) {
          return;
        }

        state.swipeUntil =
          Date.now() + SWIPE_VALID_MS;
      };

      if (window.PointerEvent) {
        swipeSurface.addEventListener(
          'pointerdown',
          markUserGalleryInteraction,
          {
            passive: true
          }
        );
      } else {
        /*
        * Fallback for older mobile browsers.
        */
        swipeSurface.addEventListener(
          'touchstart',
          () => {
            if (!MOBILE_MEDIA.matches) {
              return;
            }

            state.swipeUntil =
              Date.now() + SWIPE_VALID_MS;
          },
          {
            passive: true
          }
        );
      }

      /*
      * Dawn fires slideChanged when the visible
      * gallery image changes.
      */
      viewer.addEventListener(
        'slideChanged',
        (event) => {
          if (!MOBILE_MEDIA.matches) {
            return;
          }

          const now = Date.now();

          /*
          * Dawn itself just changed the gallery
          * because a variant was selected.
          *
          * Do not feed that back into variant selection.
          */
          if (
            now < suppressGalleryUntil
          ) {
            state.swipeUntil = 0;
            return;
          }

          /*
          * No recent real user interaction
          * with the gallery.
          */
          if (
            now > state.swipeUntil
          ) {
            return;
          }

          /*
          * Consume this interaction BEFORE selecting
          * the new variant.
          */
          state.swipeUntil = 0;

          const mediaTarget =
            event.detail
              ?.currentElement
              ?.dataset
              ?.mediaId;

          if (!mediaTarget) {
            return;
          }

          const matches =
            mediaMap.get(
              String(mediaTarget)
            );

          /*
          * Image must uniquely identify one variant.
          */
          if (
            !matches ||
            matches.length !== 1
          ) {
            return;
          }

          const entry = matches[0];

          /*
          * Already selected.
          */
          if (
            String(entry.variantId) ===
            getCurrentVariantId()
          ) {
            return;
          }

          selectVariant(entry);
        }
      );
    });
    sectionRoot.dataset
      .variantImageSyncInitialized =
      'true';
  }

  function initAll(root = document) {
    getProductRoots(root).forEach(
      initProduct
    );
  }

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => initAll()
    );
  } else {
    initAll();
  }

  /*
   * Shopify Theme Editor support.
   */
  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initAll(event.target);
    }
  );
})();